import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listCreateStubs, planCreateScreenshots, generateCreateScreenshot,
  type CreateScreenshotScene,
} from '@/api/apps-setup';
import { ApiError } from '@/api/client';
import { saveBlob } from '@/lib/icon-export';
import { Select } from '@/components/Select';

/** Play accepts at most 8 phone screenshots per locale. */
const MAX_SHOTS = 8;
/** What a set costs by default — five frames is a full Play listing. */
const DEFAULT_SHOTS = 5;

interface Row extends CreateScreenshotScene {
  id: number;
  status: 'idle' | 'generating' | 'done' | 'error';
  url?: string;
  /** MEDIA key of the drawn frame — what gets attached to the app on create. */
  key?: string;
  error?: string;
}

/**
 * Screenshot generation for an app that does not exist yet.
 *
 * The Play images panel plans its scenes from the locale's listing; at creation
 * time there is no listing and often not even a description, so the scenes are
 * anchored on the base build stub the app will ship — the operator picks one
 * and the planner is told that is what every frame must depict. The results are
 * files to download and upload to the Play Console by hand: there is no app row
 * and no draft to attach them to yet — the drawn frames are reported upwards
 * so the create call can attach them to the app row it is about to insert.
 */
export default function CreateScreenshots({ name, shortDesc, fullDesc, onKeysChange, stubId, onStubChange }: {
  name: string; shortDesc: string; fullDesc: string;
  onKeysChange?: (keys: string[]) => void;
  /** Owned by the Create tab: the picked stub also seeds the app's build config on create. */
  stubId: string; onStubChange: (id: string) => void;
}) {
  const [count, setCount] = useState(DEFAULT_SHOTS);
  const [brief, setBrief] = useState('');
  const [style, setStyle] = useState('');
  // What the app is, from the plan — sent with every frame so the last ones
  // stay on the stub instead of drifting into a generic app.
  const [subject, setSubject] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [planning, setPlanning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState('');

  const stubs = useQuery({ queryKey: ['apps-setup', 'create-stubs'], queryFn: listCreateStubs });
  const busy = planning || !!progress;

  const setField = (id: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));

  async function plan() {
    setPlanning(true); setError('');
    try {
      const out = await planCreateScreenshots({
        name, short_description: shortDesc, full_description: fullDesc,
        brief, stub_id: stubId ? Number(stubId) : undefined, count,
      });
      setStyle(out.style);
      setSubject(out.subject);
      setRows(out.scenes.map((s, i) => ({ ...s, id: i, status: 'idle' })));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Planning failed');
    } finally {
      setPlanning(false);
    }
  }

  // One frame per request: a five-image batch in a single call outlives the
  // gateway timeout, and a failure here must not discard the frames already
  // drawn — each one is a paid render.
  async function draw(targets: Row[]) {
    if (targets.length === 0) return;
    setError(''); setProgress({ done: 0, total: targets.length });
    let latest = rows;
    const patch = (id: number, p: Partial<Row>) => {
      latest = latest.map((x) => x.id === id ? { ...x, ...p } : x);
      setRows(latest);
    };

    let failures = 0;
    for (let i = 0; i < targets.length; i++) {
      const r = latest.find((x) => x.id === targets[i].id)!;
      patch(r.id, { status: 'generating', error: undefined });
      try {
        const out = await generateCreateScreenshot({
          style, subject, scene: r.scene, caption: r.caption, index: r.id, total: latest.length,
        });
        patch(r.id, { status: 'done', url: out.url, key: out.key });
      } catch (e) {
        failures++;
        patch(r.id, { status: 'error', error: e instanceof ApiError ? e.message : 'generation failed' });
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setProgress(null);
    if (failures > 0) setError(`${failures} frame(s) failed — fix the scene and redo just those.`);
  }

  function slug() {
    const s = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return s || 'app';
  }

  async function download(r: Row) {
    setError(''); setDownloading(r.url!);
    try {
      const res = await fetch(r.url!);
      if (!res.ok) throw new Error('Download failed');
      saveBlob(await res.blob(), `${slug()}-screenshot-${r.id + 1}.png`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading('');
    }
  }

  const done = rows.filter((r) => r.status === 'done');

  // Report the drawn frames, in the order they are shown, to whoever creates
  // the app: they become its store screenshots.
  useEffect(() => {
    onKeysChange?.(rows.filter((r) => r.status === 'done' && r.key).map((r) => r.key!));
  }, [rows, onKeysChange]);

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-content">Store screenshots</h3>
        <span className="text-xs text-content-subtle">1024×1536 · captions in English</span>
      </div>
      <p className="text-xs text-content-subtle">
        The app has no Play listing yet, so pick the stub it will ship: every frame is drawn as that game.
        Generated frames are saved with the app once you create it, so they can be handed over later —
        and they can be downloaded here too.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[16rem] flex-1">
          <label className="block text-xs text-content-muted mb-1">Stub</label>
          <Select
            value={stubId}
            options={(stubs.data ?? []).map((s) => ({
              value: String(s.id),
              label: s.notes ? `${s.name} · ${s.notes}` : s.name,
            }))}
            onChange={onStubChange}
            placeholder={stubs.data && stubs.data.length === 0 ? '— no stubs configured —' : '— select stub —'}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-content-muted mb-1">How many</label>
          <select
            value={count}
            disabled={busy || rows.length > 0}
            onChange={(e) => setCount(parseInt(e.target.value, 10))}
            className="px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content disabled:opacity-40"
          >
            {Array.from({ length: MAX_SHOTS }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <textarea
        placeholder="Brief (optional) — e.g. bright arcade look, show the board, the combo and the level-up screen"
        value={brief}
        disabled={busy}
        rows={2}
        onChange={(e) => setBrief(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
      />

      {rows.length === 0 ? (
        <button
          type="button"
          disabled={busy || (!stubId && !name.trim() && !brief.trim())}
          onClick={plan}
          className="px-3 py-2 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
        >
          {planning ? 'Planning...' : `Plan ${count} screenshot(s)`}
        </button>
      ) : (
        <>
          <div>
            <label className="block text-xs text-content-muted mb-1">Shared style (applied to every frame)</label>
            <textarea
              value={style}
              rows={2}
              disabled={!!progress}
              onChange={(e) => setStyle(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
            />
          </div>

          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="flex gap-3 items-start border border-border rounded-md p-2">
                {r.url && <img src={r.url} alt="" className="h-28 rounded border border-border object-cover shrink-0" />}
                <div className="flex-1 space-y-2 min-w-0">
                  <input
                    value={r.caption}
                    placeholder="Caption — leave empty for an image with no text"
                    disabled={!!progress}
                    onChange={(e) => setField(r.id, { caption: e.target.value })}
                    className="w-full px-2 py-1 text-sm rounded-md border border-border bg-surface-muted text-content"
                  />
                  <textarea
                    value={r.scene}
                    rows={2}
                    disabled={!!progress}
                    onChange={(e) => setField(r.id, { scene: e.target.value })}
                    className="w-full px-2 py-1 text-sm rounded-md border border-border bg-surface-muted text-content"
                  />
                  <div className="flex items-center gap-3">
                    {r.status === 'generating' && <span className="text-xs text-amber-500">Drawing...</span>}
                    {r.error && <span className="text-xs text-red-500">{r.error}</span>}
                    {!progress && (r.status === 'done' || r.status === 'error') && (
                      <button
                        type="button"
                        onClick={() => draw([r])}
                        className="text-xs text-content-muted hover:underline"
                      >
                        Redo this one
                      </button>
                    )}
                    {r.url && (
                      <button
                        type="button"
                        disabled={downloading !== ''}
                        onClick={() => download(r)}
                        className="text-xs text-primary hover:underline disabled:opacity-40"
                      >
                        {downloading === r.url ? '...' : 'Download PNG'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => draw(rows)}
              className="px-3 py-2 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
            >
              {progress
                ? `Drawing ${progress.done}/${progress.total}...`
                : done.length > 0 ? 'Redraw all' : `Generate ${rows.length} screenshot(s)`}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => { setRows([]); setStyle(''); setSubject(''); setError(''); }}
              className="text-xs text-content-muted hover:underline disabled:opacity-40"
            >
              Start over
            </button>
          </div>
        </>
      )}
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
}
